<template>
  <layout-default :floatingButton="false">
    <template v-slot:toolbar>
      <v-spacer />
      <exercises-filters
        :value="queryParams.query"
        :type="queryParams.type"
        :activity-time="queryParams.activityTime"
        :advanced-search-active="advancedExerciseSearchActive"
        v-on="$listeners"
        v-if="tab == 1 || tab == 2"
        @search-on:clear="searchClear"
        @search-on:input="searchInput"
        @search-on:multiple="searchMultiple"
        @search-on:run="searchRun"
        @clear-filters="clearFilters"
        @open-advanced-search="showAdvancedExerciseSearchDialog = true"
      />
      <sequences-filters
        :value="queryParams.query"
        :type="queryParams.type"
        :advanced-search-active="advancedSequenceSearchActive"
        v-on="$listeners"
        v-if="tab == 0 || tab == 3"
        @search-on:clear="searchClear"
        @search-on:input="searchInput"
        @search-on:multiple="searchMultiple"
        @search-on:run="searchRun"
        @clear-filters="clearFilters"
        @open-advanced-search="showAdvancedSequenceSearchDialog = true"
      />
    </template>
    <div class="exercises">
      <v-overlay :value="loading" z-index="4">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <v-tabs
        class="exercises__tabs"
        v-model="tab"
        fixed-tabs
        color="accent"
        @change="tabChange"
      >
        <v-tabs-slider></v-tabs-slider>
        <v-tab>{{ tenantTexts.routines.tabRoutines }}</v-tab>
        <v-tab>{{ tenantTexts.routines.tabActivities }}</v-tab>
      </v-tabs>
      <v-tabs-items class="exercises__tab-items" v-model="tab">
        <v-tab-item>
          <exercises-sequences
            :sequence-definitions="extendedSequenceDefinitions"
            :query-params="queryParams"
            :is-admin="isAdmin"
            :sharable="isAdmin"
            :sequence-folders="sequenceFolders"
            :mass-select="true"
            :advanced-search-active="advancedSequenceSearchActive"
            :pagination="pagination"
            @search-on:input="searchInput"
            @search-on:multiple="searchMultiple"
            @search-on:run="searchRun"
            @clear-filters="clearFilters"
            @create-new="newSequenceDialog = true"
            @delete="openConfirmDeleteDialog"
            @edit="toSequence"
            @duplicate="duplicateSequence"
            @rename="openRenameSequenceDialog"
            @assign="openAssignSequenceDialog"
            @share-dialog="openShareSequenceDialog"
            @assigned-dialog="openAssignedSequenceDialog"
            @open-import="openImportSequenceDialog"
            @new-folder="newSequenceFolderDialog = true"
            @rename-folder="openRenameSequenceFolderDialog"
            @remove-folder="openConfirmDeleteFolderDialog"
            @move-sequences="moveSequencesToFolder"
            @pagination-change="paginationChange"
            @get-sequence-preview="sequenceGetPreview"
          />
        </v-tab-item>
        <v-tab-item>
          <v-tabs
            class="exercises__tabs-secondary"
            v-model="tabActivities"
            fixed-tabs
            color="accent"
            @change="tabActivitiesChange"
          >
            <v-tabs-slider></v-tabs-slider>
            <v-tab>Activities</v-tab>
            <v-tab>Exercises</v-tab>
          </v-tabs>
          <exercises-activities
            :show-create-series-button="tabActivities == 1"
            :exercise-definitions="exerciseDefinitions"
            :query-params="queryParams"
            :get-resource-by-id="getResourceById"
            :progress-bars-values="progressBarsValues"
            :advanced-search-active="advancedExerciseSearchActive"
            :exercise-folders="exerciseFolders"
            :mass-select="true"
            :is-admin="isAdmin"
            :sharable="isAdmin"
            :sharable-to-administration="isCompanyUser"
            :movable="true"
            :pagination="pagination"
            @search-on:input="searchInput"
            @search-on:multiple="searchMultiple"
            @search-on:run="searchRun"
            @clear-filters="clearFilters"
            @edit-dialog="openEditExerciseDialog"
            @delete-dialog="openConfirmDeleteDialog"
            @share-dialog="openShareExerciseDialog"
            @duplicate-folder="duplicateFolder"
            @share-multiple-dialog="openShareMultipleExerciseDialog"
            @share-back-dialog="openShareExerciseBackDialog"
            @open-import="openImportExerciseDialog"
            @open-import-admin="openImportExerciseAdminDialog"
            @create-new="openNewExerciseDialog"
            @open-hover="exerciseOpenHover"
            @close-hover="exerciseCloseHover"
            @clear-hover="exerciseClearHover"
            @new-folder="newExerciseFolderDialog = true"
            @rename-folder="openRenameExerciseFolderDialog"
            @share-folder-items="openShareFolderItemsDialog"
            @remove-folder="openConfirmDeleteFolderDialog"
            @move-exercises="moveExercisesToFolder"
            @get-exercise-preview="exerciseGetPreview"
            @pagination-change="paginationChange"
            v-on="$listeners"
          />
        </v-tab-item>
      </v-tabs-items>

      <v-speed-dial
        v-if="tab == 1"
        v-model="fab"
        direction="top"
        :bottom="true"
        :right="true"
        fixed
        transition="slide-y-reverse-transition"
      >
        <template v-slot:activator>
          <v-btn v-model="fab" color="accent" dark elevation="0" fab large>
            <v-icon v-if="fab">
              bhi-close
            </v-icon>
            <v-icon v-else>
              bhi-plus
            </v-icon>
          </v-btn>
        </template>
        <v-btn
          rounded
          small
          @click="openNewExerciseDialog('ACTIVITY', false, true)"
          color="#ED8938"
        >
          <v-icon size="12">bhi-activity</v-icon>
          {{ tenantTexts.routines.createAMPMActivity }}
        </v-btn>
        <v-btn
          rounded
          small
          @click="openNewExerciseDialog('EXERCISE', false, true)"
          color="#945EFF"
        >
          <v-icon size="12">bhi-exercise</v-icon>
          Create Exercise
        </v-btn>
        <v-btn
          v-if="!isAdmin"
          rounded
          small
          @click="toSmartExercises"
          color="accent"
        >
          <v-icon size="12">bhi-download</v-icon>
          Import new
        </v-btn>
        <v-btn
          v-else
          rounded
          small
          @click="openImportExerciseAdminDialog()"
          color="accent"
        >
          <v-icon size="12">bhi-download</v-icon>
          Import new
        </v-btn>
      </v-speed-dial>

      <preview-zoom-dialog
        :dialogType="dialogType"
        :imageSrc="imageSrc"
        :dialogFile="dialogFile"
        :dialogFileMlUrl="dialogFileMlUrl"
        :name="imageName"
        @close-dialog="closeMediaDialog"
        @reload-resource="reloadResource"
      />
      <advanced-sequence-definition-search-dialog
        :show-dialog="showAdvancedSequenceSearchDialog"
        :clear-filter-trigger="clearFilterTrigger"
        @close-dialog="showAdvancedSequenceSearchDialog = false"
        @filter="advancedSequenceSearchFilter"
      />
      <alternative-exercises-dialog
        :key="'altenativeExercisesKey-' + alternativeExercisesKey"
        :exercise-id="exerciseDefinition ? exerciseDefinition.id : null"
        :show-dialog="showAlternativeExercisesDialog"
        :exercise-definitions="exerciseSingleDefinitions"
        :exercise-definitions-loading="loading"
        :alternative-exercises="alternativeExercisesList"
        :add-alternatives="false"
        :participants-exercises="false"
        :pagination="dialogPagination"
        @close-select-dialog="closeSelectAlternativeExercisesDialog"
        @on-select="chosenAlternativeExercises"
        v-on="$listeners"
      />
      <exercise-definition-dialog
        :show-dialog="showExerciseDialog"
        :key="exerciseDialogKey"
        @close-exercise-dialog="closeExerciseDialog"
        @show-bigger-image="showBiggerImage"
        @on-submit="createNewExerciseDefinition"
        @on-edit="editExerciseDefinition"
        @on-series-search="loadDialogExerciseDefinitions"
        @reload-resource="reloadResource"
        @reload-exercise-resource="reloadExerciseResource"
        @reload-exercise-resource-additional="reloadExerciseResourceAdditional"
        @clear-resource="clearExerciseResource"
        @clear-resource-additional="clearExerciseResourceAdditional"
        @clear-timeout="clearGetExerciseDefinitionsDialogTimeout"
        @open-alternative-exercises="showAlternativeExercisesDialog = true"
        :edited-exercise="exerciseDefinition"
        :exercise-definitions="exerciseSingleDefinitions"
        :exercise-definitions-loading="loading"
        :pagination="dialogPagination"
        :exercise-resource="exerciseResource ? exerciseResource : false"
        :exercise-resource-additional="
          exerciseResourceAdditional ? exerciseResourceAdditional : false
        "
        :parameters-enabled="true"
        :get-resource="getResource"
        :get-mime-types="getMimeTypes"
        :initial-mode="exerciseDialogMode"
        :disable-cancel="exerciseDialogDisableCancel"
        :hide-prev="exerciseDialogHidePrev"
        :cancelable="true"
        v-on="$listeners"
      />
      <sequence-assign-dialog
        :show-dialog="showAssignSequenceDialog"
        :key="sequenceDialogKey"
        :is-loading="assignSequenceLoading"
        @assign-participants="assignSequenceDefinition"
        @close-dialog="closeSequenceDialog"
      />
      <exercise-share-dialog
        :show-dialog="showShareExerciseDialog"
        :key="exerciseShareDialogKey"
        @share-practices="shareExercisePractice"
        @close-dialog="closeShareExerciseDialog"
      />
      <exercise-import-dialog
        :show-dialog="showImportExerciseDialog"
        :exercise-definitions="dialogExerciseDefinitions"
        :exercise-definitions-loading="loading"
        @search-on:input="searchInputImport"
        @search-on:run="searchRunImport"
        @load-shared-exercises="getSharedExerciseDefinitions"
        @import-exercise="importExercise"
        @close-dialog="closeDialogs"
        @get-exercise-preview="exerciseGetPreview"
      />
      <exercise-import-companies-dialog
        :show-dialog="showImportExerciseCompaniesDialog"
        :exercise-definitions="dialogExerciseDefinitions"
        :exercise-definitions-loading="loading"
        @search-on:input="searchInputImport"
        @search-on:run="searchRunImport"
        @load-shared-exercises="getSharedExerciseDefinitions"
        @import-exercise="importExercise"
        @close-dialog="closeDialogs"
        @reload-exercises="reloadExerciseDefinitions"
        @get-exercise-preview="exerciseGetPreview"
      />
      <sequence-import-dialog
        :show-dialog="showImportSequenceDialog"
        :sequence-definitions="dialogSequenceDefinitions"
        :sequence-definitions-loading="loading"
        @search-on:input="searchInputImport"
        @search-on:run="searchRunImport"
        @load-shared-sequences="getSharedSequenceDefinitions"
        @import-sequence="importSequence"
        @close-dialog="closeDialogs"
      />
      <sequence-share-dialog
        :show-dialog="showShareSequenceDialog"
        :key="sequenceShareDialogKey"
        @share-practices="shareSequencePractice"
        @close-dialog="closeShareSequenceDialog"
      />
      <sequence-assigned-dialog
        :show-dialog="showAssignedSequenceDialog"
        :key="sequenceAssignedDialogKey"
        :is-loading="loadingSequenceDefinitionParticipants"
        :assigned-participants="sequenceDefinitionParticipants"
        @close-dialog="closeAssignedSequenceDialog"
      />
      <preview-exercise-dialog
        :show-dialog="showExercisePreviewDialog"
        @close-dialog="closeDialogs"
        @preview-media="openMediaPreview"
        :exercise="exerciseDefinition"
      />
      <preview-sequence-dialog
        :show-dialog="showSequencePreviewDialog"
        @close-dialog="closeDialogs"
        @preview-media="openMediaPreview"
        :sequence="sequenceDefinition"
      />
      <v-dialog
        v-model="showConfirmDeleteDialog"
        persistent
        max-width="500"
        @click:outside="onClickOutsideDeleteExerciseDialog"
        @keydown="onKeydownDeleteExerciseDialog"
      >
        <v-card>
          <v-card-title class="headline"
            >Are you sure you want to delete this item? 🥵</v-card-title
          >
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn class="rounded-pill" text @click="closeConfirmDeleteDialog"
              >No</v-btn
            >
            <v-btn
              class="rounded-pill elevation-0"
              color="accent"
              @click="
                itemTypeName === 'exercises'
                  ? openDeleteExerciseDialog()
                  : openDeleteSequenceDialog()
              "
              >Yes</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="showConfirmDeleteFolderDialog"
        persistent
        max-width="500"
        @click:outside="onClickOutsideRemoveExerciseFolder"
        @keydown="onKeydownRemoveExerciseFolder"
      >
        <v-card>
          <v-card-title class="headline"
            >Are you sure you want to delete this item? 🥵</v-card-title
          >
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              class="rounded-pill"
              text
              @click="closeConfirmDeleteFolderDialog"
              >No</v-btn
            >
            <v-btn
              class="rounded-pill elevation-0"
              color="accent"
              @click="
                deleteFolderType === 'exercises'
                  ? openRemoveExerciseFolderDialog()
                  : openRemoveSequenceFolderDialog()
              "
              >Yes</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="showDeleteExerciseDialog"
        persistent
        max-width="400"
        @click:outside="onClickOutsideDeleteExerciseDialog"
        @keydown="onKeydownDeleteExerciseDialog"
      >
        <v-card>
          <v-card-title class="headline">{{
            tenantTexts.routines.removeThisActivity.title
          }}</v-card-title>
          <v-card-actions class="v-card__actions--flexcenter">
            <v-btn
              class="rounded-pill"
              text
              @click="
                closeDialogs()
                exerciseId = null
              "
              >Cancel</v-btn
            >
            <v-btn
              class="rounded-pill elevation-0"
              color="accent"
              @click="deleteExerciseHandler"
              >Confirm</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="newExerciseFolderDialog"
        persistent
        max-width="290"
        @click:outside="onClickOutsideNewExerciseFolder"
        @keydown="onKeydownNewExerciseFolder"
      >
        <v-card>
          <v-card-title class="headline"
            >{{
              editExerciseFolderId ? 'Rename' : 'Add new'
            }}
            folder</v-card-title
          >
          <v-card-text>
            <v-text-field
              label="Folder name"
              v-model="newExerciseFolderName"
              required
            ></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              class="rounded-pill"
              text
              @click="closeNewExerciseFolderDialog"
              >Cancel</v-btn
            >
            <v-btn
              class="rounded-pill elevation-0"
              color="accent"
              :disabled="newExerciseFolderName == ''"
              @click="prepareNewExerciseFolder"
              >{{ editExerciseFolderId ? 'Save' : 'Create' }}</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="removeExerciseFolderDialog"
        persistent
        max-width="290"
        @click:outside="onClickOutsideRemoveExerciseFolder"
        @keydown="onKeydownRemoveExerciseFolder"
      >
        <v-card>
          <v-card-title class="headline">
            Remove folder
            <v-spacer />
            <v-btn
              text
              icon
              small
              @click="
                removeExerciseFolderDialog = false
                editExerciseFolderId = null
              "
            >
              <v-icon>bhi-close-circle</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            {{ tenantTexts.routines.removeExerciseFolder.content }}
          </v-card-text>
          <v-card-actions class="justify-center pb-0">
            <v-btn
              class="rounded-pill"
              :elevation="0"
              color="accent"
              @click="prepareRemoveExerciseFolder(true)"
              >{{
                tenantTexts.routines.removeExerciseFolder.optionRemoveAll
              }}</v-btn
            >
          </v-card-actions>
          <v-card-actions class="justify-center pb-6">
            <v-btn
              class="rounded-pill"
              :elevation="0"
              color="accent"
              @click="prepareRemoveExerciseFolder(false)"
              >{{
                tenantTexts.routines.removeExerciseFolder.optionRemoveFolderOnly
              }}</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="removeSequenceFolderDialog"
        persistent
        max-width="290"
        @click:outside="onClickOutsideRemoveSequenceFolder"
        @keydown="onKeydownRemoveSequenceFolder"
      >
        <v-card>
          <v-card-title class="headline">
            Remove folder
            <v-spacer />
            <v-btn
              text
              icon
              small
              @click="
                removeSequenceFolderDialog = false
                editSequenceFolderId = null
              "
            >
              <v-icon>bhi-close-circle</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            Are you really sure you want to delete this for EVERYONE?
          </v-card-text>
          <v-card-actions class="justify-center pb-0">
            <v-btn
              class="rounded-pill"
              :elevation="0"
              color="accent"
              @click="prepareRemoveSequenceFolder(true)"
              >Remove all Routines and Folder</v-btn
            >
          </v-card-actions>
          <v-card-actions class="justify-center pb-6">
            <v-btn
              class="rounded-pill"
              :elevation="0"
              color="accent"
              @click="prepareRemoveSequenceFolder(false)"
              >Remove Folder and keep Routines</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="newSequenceFolderDialog"
        persistent
        max-width="290"
        @click:outside="onClickOutsideNewSquenceFolder"
        @keydown="onKeydownNewSquenceFolder"
      >
        <v-card>
          <v-card-title class="headline"
            >{{
              editSequenceFolderId ? 'Rename' : 'Add new'
            }}
            folder</v-card-title
          >
          <v-card-text>
            <v-text-field
              label="Folder name"
              v-model="newSequenceFolderName"
              required
            ></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              class="rounded-pill"
              text
              @click="closeNewSequenceFolderDialog"
              >Cancel</v-btn
            >
            <v-btn
              class="rounded-pill elevation-0"
              color="accent"
              :disabled="newSequenceFolderName == ''"
              @click="prepareNewSequenceFolder"
              >{{ editSequenceFolderId ? 'Save' : 'Create' }}</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="newSequenceDialog"
        persistent
        max-width="290"
        @click:outside="onClickOutsideNewSquence"
        @keydown="onKeydownNewSquence"
      >
        <v-card>
          <v-card-title class="headline">Add new routine</v-card-title>
          <v-card-text>
            <v-text-field
              label="Routine name"
              v-model="newSequenceName"
              required
            ></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn class="rounded-pill" text @click="closeNewSequenceDialog"
              >Cancel</v-btn
            >
            <v-btn
              class="rounded-pill elevation-0"
              color="accent"
              :disabled="newSequenceName == ''"
              @click="createNewSequence"
              >Create</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="showDeleteSequenceDialog"
        persistent
        max-width="400"
        @click:outside="onClickOutsideDeleteSequence"
        @keydown="onKeydownDeleteSequence"
      >
        <v-card>
          <v-card-title class="headline"
            >Are you really sure you want to delete this for
            EVERYONE?</v-card-title
          >
          <v-card-actions class="v-card__actions--flexcenter">
            <v-btn
              class="rounded-pill"
              text
              @click="
                closeDialogs()
                sequenceId = null
              "
              >Cancel</v-btn
            >
            <v-btn
              class="rounded-pill elevation-0"
              color="accent"
              @click="deleteSequenceHandler"
              >Confirm</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="showRenameSequenceDialog"
        persistent
        max-width="400"
        @click:outside="onClickOutsideRenameSequence"
        @keydown="onKeydownRenameSequence"
      >
        <v-card>
          <v-card-title class="headline">Rename routine</v-card-title>
          <v-card-text>
            <v-text-field
              label="Routine name"
              v-model="newSequenceName"
              required
            ></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              class="rounded-pill"
              text
              @click="
                closeDialogs()
                sequenceId = null
              "
              >Cancel</v-btn
            >
            <v-btn
              :disabled="newSequenceName == oldSequenceName"
              class="rounded-pill elevation-0"
              color="accent"
              @click="renameSequenceHandler"
              >Confirm</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="exerciseHover"
        hide-overlay
        attach=".exercises"
        content-class="exercises-hover-dialog"
        @click:outside="onClickOutsideExerciseHover"
        @keydown="onKeydownExerciseHover"
      >
        <v-card @mouseleave.native.stop="exerciseCloseHover()">
          <img
            v-if="exerciseHoverUrl && exerciseHoverType == 'base64'"
            :src="'data:image/jpeg;base64,' + exerciseHoverUrl"
          />
          <img
            v-else-if="exerciseHoverUrl && exerciseHoverType == 'url'"
            :src="exerciseHoverUrl"
          />
          <v-btn color="white" icon size="24" @click="exerciseCloseHover">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="showShareExerciseBackDialog"
        persistent
        max-width="400"
        @click:outside="onClickOutsideShareExerciseBack"
        @keydown="onKeydownShareExerciseBack"
      >
        <v-card>
          <v-card-title class="headline">Share</v-card-title>
          <v-card-text> Confirm sharing to {{ mainTitle }} Team </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              class="rounded-pill"
              text
              @click="
                closeDialogs()
                exerciseId = null
              "
              >Cancel</v-btn
            >
            <v-btn
              class="rounded-pill elevation-0"
              color="accent"
              @click="prepareShareExerciseBack"
              >Confirm</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="showExerciseFoldersErrorsModal"
        max-width="500"
        persistent
        @click:outside="onClickOutsideShareExerciseBack"
        @keydown="onKeydownExerciseHover"
      >
        <v-card>
          <v-card-title
            >You already shared those items with Companies:</v-card-title
          >
          <v-list
            class="px-3"
            v-for="company in duplicatedCompanies"
            :key="company.companyId"
          >
            <v-list-item class="exercises__dialog__list"
              >- {{ company.companyName }}</v-list-item
            >
          </v-list>
          <v-card-title
            >All items successfully shared with other Companies.</v-card-title
          >
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              class="rounded-pill elevation-0"
              color="accent"
              @click="
                closeDialogs()
                closeExerciseFolderErrorDialog()
              "
              >Ok</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </layout-default>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import LayoutDefault from '@/layouts/LayoutDefault.vue'
import ExerciseDefinitionDialog from '@/components/dialogs/ExerciseDefinitionDialog.vue'
import AdvancedSequenceDefinitionSearchDialog from '@/components/dialogs/AdvancedSequenceDefinitionSearchDialog.vue'
import SequenceAssignDialog from '@/components/dialogs/SequenceAssignDialog.vue'
import ExerciseShareDialog from '@/components/dialogs/ExerciseShareDialog.vue'
import ExerciseImportDialog from '@/components/dialogs/ExerciseImportDialog.vue'
import SequenceImportDialog from '@/components/dialogs/SequenceImportDialog.vue'
import PreviewExerciseDialog from '@/components/dialogs/PreviewExerciseDialog.vue'
import PreviewSequenceDialog from '@/components/dialogs/PreviewSequenceDialog.vue'
import SequenceShareDialog from '@/components/dialogs/SequenceShareDialog.vue'
import RouterMixin from '@/mixins/router'
import PreviewZoomDialog from '@/components/dialogs/PreviewZoomDialog.vue'
import ExercisesActivities from '@/components/exercises/ExercisesActivities.vue'
import ExercisesSequences from '@/components/exercises/ExercisesSequences.vue'
import ExercisesFilters from '@/components/exercises/ExercisesFilters.vue'
import SequencesFilters from '@/components/exercises/SequencesFilters.vue'
import ExerciseImportCompaniesDialog from '@/components/dialogs/ExerciseImportCompaniesDialog.vue'
import AlternativeExercisesDialog from '@/components/dialogs/AlternativeExercisesDialog.vue'
import SequenceAssignedDialog from '@/components/dialogs/SequenceAssignedDialog.vue'
import TenantTextsMixin from '@/mixins/tenantTexts'
import isStringBase64 from '@/helpers/isStringBase64.js'

import { debounce } from '@/helpers/debounce'

export default {
  name: 'Exercises',
  components: {
    LayoutDefault,
    ExerciseDefinitionDialog,
    AdvancedSequenceDefinitionSearchDialog,
    SequenceAssignDialog,
    ExerciseShareDialog,
    ExerciseImportDialog,
    SequenceImportDialog,
    SequenceShareDialog,
    PreviewZoomDialog,
    ExercisesActivities,
    ExercisesSequences,
    PreviewExerciseDialog,
    PreviewSequenceDialog,
    ExercisesFilters,
    SequencesFilters,
    ExerciseImportCompaniesDialog,
    AlternativeExercisesDialog,
    SequenceAssignedDialog,
  },
  data() {
    return {
      headers: [
        { text: 'Name', value: 'name' },
        { text: 'Description', value: 'description' },
        { text: 'Date added', value: 'creationDate' },
      ],
      defaultQueryParams: {
        pageSize: 50,
      },
      exerciseDialogKey: 1,
      sequenceDialogKey: 999,
      exerciseShareDialogKey: 1999,
      sequenceShareDialogKey: 2999,
      sequenceAssignedDialogKey: 3999,
      showExerciseDialog: false,
      exerciseDialogMode: false,
      exerciseDialogDisableCancel: false,
      exerciseDialogHidePrev: false,
      showDeleteExerciseDialog: false,
      showShareExerciseDialog: false,
      showAssignedSequenceDialog: false,
      showShareSequenceDialog: false,
      showShareExerciseBackDialog: false,
      showDeleteSequenceDialog: false,
      showRenameSequenceDialog: false,
      showAssignSequenceDialog: false,
      showAdvancedExerciseSearchDialog: false,
      showAdvancedSequenceSearchDialog: false,
      showExercisePreviewDialog: false,
      showSequencePreviewDialog: false,
      showImportExerciseDialog: false,
      showImportExerciseCompaniesDialog: false,
      showImportSequenceDialog: false,
      newExerciseFolderName: '',
      newSequenceFolderName: '',
      newExerciseFolderDialog: false,
      removeExerciseFolderDialog: false,
      removeSequenceFolderDialog: false,
      editExerciseFolderId: false,
      editSequenceFolderId: false,
      newSequenceFolderDialog: false,
      exerciseId: null,
      selectedExercises: null,
      exerciseType: null,
      sequenceId: null,
      dialogType: 0,
      imageSrc: false,
      imageName: false,
      dialogFile: null,
      dialogFileMlUrl: null,
      exerciseResource: null,
      exerciseResourceAdditional: null,
      newSequenceDialog: false,
      oldSequenceName: '',
      newSequenceName: '',
      exerciseHover: false,
      exerciseHoverUrl: null,
      exerciseHoverType: 'base64',
      exerciseHoverTimeout: null,
      exerciseHoverInterval: null,
      progressBarsValues: {},
      tab: 0,
      tabActivities: 0,
      advancedExerciseSearchActive: false,
      advancedSequenceSearchActive: false,
      clearFilterTrigger: 0,
      fab: false,
      urls: [
        '/routines',
        '/activities',
        '/smart-activities',
        '/smart-sequences',
      ],
      alternativeExercisesKey: 0,
      showAlternativeExercisesDialog: false,
      alternativeExercisesList: [],
      showConfirmDeleteDialog: false,
      deleteItemId: null,
      deleteItemType: null,
      itemTypeName: null,
      showConfirmDeleteFolderDialog: false,
      deleteFolderId: null,
      deleteFolderType: null,
      showExerciseFoldersErrorsModal: false,
      companyAlreadyItemsShared: [],
      assignSequenceLoading: false,
    }
  },
  mixins: [RouterMixin, TenantTextsMixin],
  computed: {
    ...mapState('exercisesStates', [
      'exerciseDefinitions',
      'dialogExerciseDefinitions',
      'exerciseDefinition',
      'sequenceDefinitions',
      // 'profilePreviews',
      'dialogSequenceDefinitions',
      'sequenceDefinition',
      'loading',
      'pagination',
      'dialogPagination',
      'exerciseFolders',
      'sequenceFolders',
      'sharingCompanies',
      'duplicatedCompanies',
      'sequenceDefinitionParticipants',
      'loadingSequenceDefinitionParticipants',
    ]),
    ...mapState('profilePreviewsStates', ['profilePreviews']),
    ...mapState('resourcesStates', ['resource']),
    ...mapGetters('userStates', ['getRolename']),
    ...mapState('dictionariesStates', [
      'dictionaryBhUsersRoleIds',
      'dictionaryPracticeUserRole',
    ]),
    ...mapGetters('dictionariesStates', ['getMimeTypes']),
    ...mapState('practicesStates', ['practice']),
    isAdmin() {
      return (
        this.dictionaryBhUsersRoleIds[this.getRolename] ===
        this.dictionaryBhUsersRoleIds.Admin
      )
    },
    isCompanyUser() {
      return this.dictionaryPracticeUserRole[this.getRolename] ? true : false
    },
    exerciseSingleDefinitions() {
      let arr = []
      this.dialogExerciseDefinitions.forEach(element => {
        if (element.type == 'EXERCISE') {
          let obj = {}
          for (const [key, value] of Object.entries(element.exercise)) {
            obj[key] = value
          }
          arr.push(obj)
        }
      })
      return arr
    },
    mainTitle() {
      return this.extendedConfig?.VUE_MAIN_TITLE || 'Keep Rising'
    },
    extendedSequenceDefinitions() {
      return this.sequenceDefinitions.map(definition => {
        if (definition.participants?.items?.length > 0) {
          definition.participants.items = definition.participants.items.map(
            participant => {
              return {
                ...participant,
                ...this.profilePreviews.find(
                  profile => profile.profileId === participant.profileId,
                ),
              }
            },
          )
        }
        return definition
      })
    },
  },
  watch: {
    duplicatedCompanies() {
      if (this.duplicatedCompanies.length > 0) {
        this.showExerciseFoldersErrorsModal = true
      }
    },
  },
  created() {
    let ind = this.urls.findIndex(item => item == this.$route.path)
    this.tab = ind > -1 ? ind : 0
    window.onpopstate = event => {
      ind = this.urls.findIndex(item => item == window.location.pathname)
      this.tab = ind > -1 ? ind : 0
    }
    this.getDataAction(this.queryParams, false, true)
  },
  methods: {
    isStringBase64,
    ...mapActions('exercisesStates', [
      'getExerciseDefinition',
      'getExerciseDefinitions',
      'getSharedExerciseDefinitions',
      'getDialogExerciseDefinitions',
      'addExerciseDefinition',
      'changeExerciseDefinition',
      'deleteExerciseDefinition',
      'getSequenceDefinition',
      'getSequenceDefinitions',
      'getSharedSequenceDefinitions',
      'addSequenceDefinition',
      'changeSequenceDefinition',
      'deleteSequenceDefinition',
      'duplicateSequenceDefinition',
      'publishSequenceDefinition',
      'assignSequence',
      'clearGetExerciseDefinitionsDialogTimeout',
      'shareExerciseToPractice',
      'shareFolderItemsToPractice',
      'shareSequenceToPractice',
      'shareExerciseDefinition',
      'shareSequenceDefinition',
      'shareExerciseBack',
      'getExerciseFolders',
      'getSequenceFolders',
      'createNewExerciseFolder',
      'renameExerciseFolder',
      'renameSequenceFolder',
      'removeExerciseFolder',
      'removeSequenceFolder',
      'createNewSequenceFolder',
      'moveExerciseItem',
      'moveSequenceItem',
      'getSharedExerciseDefinitionsCompanies',
      'duplicateExerciseFolder',
      'getSequenceDefinitionParticipants',
    ]),
    ...mapActions('resourcesStates', ['getResource', 'getResourceById']),
    ...mapMutations('exercisesStates', [
      'setExerciseDefinition',
      'setDuplicatedCompanies',
      'setSequenceDefinitionParticipants',
    ]),
    createNewSequence() {
      let data = {
        title: this.newSequenceName,
      }
      if (this.queryParams?.folderId) data.folderId = this.queryParams.folderId
      this.addSequenceDefinition(data).then(response => {
        this.toSequence(this.sequenceDefinition.id)
      })
    },
    toSequence(id) {
      this.$router
        .push({
          name: 'Sequence',
          path: '/routines/routine/:id',
          params: { id: id },
        })
        .catch(() => {})
    },
    closeNewSequenceDialog() {
      this.newSequenceName = ''
      this.oldSequenceName = ''
      this.newSequenceDialog = false
    },
    duplicateSequence(sequenceId) {
      this.duplicateSequenceDefinition(sequenceId)
    },
    duplicateFolder(folderId) {
      this.duplicateExerciseFolder(folderId)
    },
    publishSequence(sequenceId) {
      this.publishSequenceDefinition(sequenceId)
    },
    openNewExerciseDialog(mode, disableCancel = false, hidePrev = false) {
      if (mode) this.exerciseDialogMode = mode
      if (disableCancel) this.exerciseDialogDisableCancel = true
      if (hidePrev) this.exerciseDialogHidePrev = true
      this.setExerciseDefinition(false)
      this.showExerciseDialog = true
    },
    createNewExerciseDefinition(data) {
      if (this.$route.query?.folderId)
        data.folderId = this.$route.query.folderId
      this.addExerciseDefinition({
        data: data,
      }).then(() => {
        this.closeExerciseDialog()
      })
    },
    editExerciseDefinition(data) {
      this.changeExerciseDefinition({
        data: data,
      }).then(() => {
        this.closeExerciseDialog()
      })
    },
    searchClear() {},
    searchInput({ key, value }) {
      this.getDataAction({ ...this.defaultQueryParams, [key]: value })
    },
    searchInputImport({ key, value }) {
      this.getSharedExerciseDefinitions({ params: { [key]: value } })
    },
    searchMultiple(list) {
      let query = { ...this.defaultQueryParams }
      list.forEach(item => {
        query = { ...query, [item.key]: item.value }
      })
      this.getDataAction(query)
    },
    searchRun(search) {
      this.getDataAction({ ...this.defaultQueryParams, title: search })
    },
    searchRunImport(search) {
      this.getSharedExerciseDefinitions({
        ...this.defaultQueryParams,
        query: search,
      })
    },
    getData(data) {
      debounce(
        () => {
          switch (this.tab) {
            case 1:
              if (this.tabActivities == 0) data.set('type', 'ACTIVITY')
              if (this.tabActivities == 1) data.set('type', 'EXERCISE,SERIES')
              this.getExerciseDefinitions({ params: data })
              this.getExerciseFolders()
              break
            case 0:
              this.getSequenceDefinitions(data)
              this.getSequenceFolders()
              break
            default:
              break
          }
        },
        100,
        'getData',
      )
    },
    openEditExerciseDialog(id, type) {
      this.getExerciseDefinition({ id: id, type: type }).then(() => {
        this.showExerciseDialog = true
      })
    },
    openDeleteExerciseDialog() {
      this.showConfirmDeleteDialog = false
      this.showDeleteExerciseDialog = true
    },
    openShareExerciseDialog(id, type) {
      this.showShareExerciseDialog = true
      this.exerciseId = id
      this.exerciseType = type
    },
    openShareMultipleExerciseDialog(selectedExercises) {
      this.showShareExerciseDialog = true
      this.selectedExercises = selectedExercises
    },
    openShareExerciseBackDialog(id, type) {
      this.showShareExerciseBackDialog = true
      this.exerciseId = id
      this.exerciseType = type
    },
    openShareSequenceDialog(id, type) {
      this.showShareSequenceDialog = true
      this.sequenceId = id
    },
    openAssignedSequenceDialog(id, type) {
      this.showAssignedSequenceDialog = true
      this.setSequenceDefinitionParticipants = []
      if (id) this.getSequenceDefinitionParticipants({ id })
      this.sequenceId = id
    },
    openDeleteSequenceDialog() {
      this.showConfirmDeleteDialog = false
      this.showDeleteSequenceDialog = true
    },
    openRenameSequenceDialog(id, title) {
      this.showRenameSequenceDialog = true
      this.newSequenceName = title
      this.oldSequenceName = title
      this.sequenceId = id
    },
    openAssignSequenceDialog(id) {
      this.showAssignSequenceDialog = true
      this.sequenceId = id
    },
    async deleteExerciseHandler() {
      this.deleteExerciseDefinition({
        id: this.deleteItemId,
        type: this.deleteItemType,
      }).then(() => {
        this.closeDialogs()
      })
    },
    async deleteSequenceHandler() {
      this.deleteSequenceDefinition(this.deleteItemId).then(() => {
        this.closeDialogs()
      })
    },
    async renameSequenceHandler() {
      this.changeSequenceDefinition({
        sequenceId: this.sequenceId,
        title: this.newSequenceName,
      }).then(() => {
        this.closeDialogs()
      })
    },
    assignSequenceDefinition(participants, date) {
      this.assignSequenceLoading = true
      let data = {
        startDate: date,
        participants: [],
      }
      participants.forEach(participant => {
        data.participants.push({ participantId: participant })
      })
      this.assignSequence({ sequenceId: this.sequenceId, data: data })
        .then(() => {
          this.assignSequenceLoading = false
          this.closeSequenceDialog()
          this.getData()
        })
        .catch(() => {
          this.assignSequenceLoading = false
        })
    },
    shareExercisePractice(practices) {
      this.closeShareExerciseDialog()
      if (this.exerciseId)
        this.shareExerciseToPractice({
          exerciseId: this.exerciseId,
          type: this.exerciseType,
          practices: practices,
        })
      else if (this.selectedExercises?.length > 0)
        this.selectedExercises.forEach(exercise => {
          this.shareExerciseToPractice({
            exerciseId: exercise.typeId,
            type: exercise.type,
            practices: practices,
          })
        })
      else if (this.editExerciseFolderId) {
        this.shareFolderItemsToPractice({
          folderId: this.editExerciseFolderId,
          practices: practices,
        })
      }
      this.editExerciseFolderId = null
      this.selectedExercises = null
      this.exerciseId = null
      this.exerciseType = null
    },
    shareSequencePractice(practices) {
      this.closeShareSequenceDialog()
      this.shareSequenceToPractice({
        sequenceId: this.sequenceId,
        practices: practices,
      })
    },
    prepareShareExerciseBack() {
      this.shareExerciseBack({
        exerciseId: this.exerciseId,
        type: this.exerciseType,
      })
      this.closeDialogs()
    },
    closeExerciseDialog() {
      this.showExerciseDialog = false
      this.exerciseDialogMode = false
      this.exerciseDialogKey++
      this.setExerciseDefinition(false)
      this.exerciseResource = null
      this.exerciseResourceAdditional = null
      this.exerciseDialogDisableCancel = false
      this.exerciseDialogHidePrev = false
      this.clearGetExerciseDefinitionsDialogTimeout()
    },
    closeSequenceDialog() {
      this.showAssignSequenceDialog = false
      this.sequenceDialogKey++
    },
    closeShareExerciseDialog() {
      this.showShareExerciseDialog = false
      this.exerciseShareDialogKey++
    },
    closeShareSequenceDialog() {
      this.showShareSequenceDialog = false
      this.sequenceShareDialogKey++
    },
    closeAssignedSequenceDialog() {
      this.showAssignedSequenceDialog = false
      this.sequenceAssignedDialogKey++
    },
    closeExerciseFolderErrorDialog() {
      this.showExerciseFoldersErrorsModal = false
    },
    closeDialogs() {
      this.showDeleteExerciseDialog = false
      this.showShareExerciseDialog = false
      this.showShareSequenceDialog = false
      this.showAssignedSequenceDialog = false
      this.showDeleteSequenceDialog = false
      this.showRenameSequenceDialog = false
      this.showAssignSequenceDialog = false
      this.showExercisePreviewDialog = false
      this.showSequencePreviewDialog = false
      this.showImportExerciseDialog = false
      this.showImportExerciseCompaniesDialog = false
      this.showImportSequenceDialog = false
      this.showShareExerciseBackDialog = false
      this.showConfirmDeleteDialog = false
      this.showExerciseFoldersErrorsModal = false
      this.setDuplicatedCompanies([])
    },
    reloadResource(id) {
      this.getResource({ id: id })
    },
    reloadExerciseResource(id) {
      this.getResourceById({ resourceId: id }).then(response => {
        this.exerciseResource = response.data
      })
    },
    reloadExerciseResourceAdditional(id) {
      this.getResourceById({ resourceId: id }).then(response => {
        this.exerciseResourceAdditional = response.data
      })
    },
    clearExerciseResource() {
      this.exerciseResource = null
    },
    clearExerciseResourceAdditional() {
      this.exerciseResourceAdditional = null
    },
    showBiggerImage({ url, file, name, mlResourceUrl = null, type = 1 }) {
      this.imageSrc = url
      this.dialogFile = file
      this.imageName = name
      this.dialogFileMlUrl = mlResourceUrl
      this.dialogType = type
    },
    closeMediaDialog() {
      this.dialogType = 0
      this.imageName = null
      this.currentFile = null
      this.imageSrc = null
      this.dialogFileMlUrl = null
    },
    placeholder(fileTypeId) {
      let image = 'placeholder_other.png'
      switch (fileTypeId) {
        case 17:
          image = 'placeholder_other.png'
          break
        case 18:
          image = 'placeholder_image.png'
          break
        case 19:
          image = 'placeholder_video.png'
          break
        case 20:
          image = 'placeholder_pdf.png'
          break
        case 21:
          image = 'placeholder_doc.png'
          break
        case 22:
          image = 'placeholder_presentation.png'
          break
        case 23:
          image = 'placeholder_sheet.png'
          break
        default:
          break
      }
      return require('@/assets/' + image)
    },
    exerciseOpenHover(
      id,
      media = false,
      mobileStaticMedia = null,
      additionalMedia = null,
    ) {
      clearTimeout(this.exerciseHoverTimeout)
      this.exerciseCloseHover()
      if (
        id &&
        (media?.thumbnailUrl ||
          media?.previewUrl ||
          additionalMedia?.thumbnailUrl ||
          additionalMedia?.previewUrl ||
          mobileStaticMedia?.mediaUrl)
      ) {
        this.progressBarsValues = { ...this.progressBarsValues, ...{ [id]: 1 } }
        if (this.exerciseHoverInterval)
          clearInterval(this.exerciseHoverInterval)
        this.exerciseHoverInterval = setInterval(() => {
          if (this.progressBarsValues[id] >= 100) {
            clearInterval(this.exerciseHoverInterval)
            return
          }
          let tmp = this.progressBarsValues[id]
          this.progressBarsValues = {
            ...this.progressBarsValues,
            ...{ [id]: tmp + 33 },
          }
        }, 200)
        if (this.exerciseHoverTimeout) clearTimeout(this.exerciseHoverTimeout)
        this.exerciseHoverTimeout = setTimeout(() => {
          this.exerciseHover = null
          if (
            media?.previewUrl ||
            additionalMedia?.previewUrl ||
            mobileStaticMedia?.mediaUrl
          ) {
            this.exerciseHoverUrl =
              media?.previewUrl ||
              additionalMedia?.previewUrl ||
              mobileStaticMedia?.mediaUrl
            this.exerciseHoverType = this.isStringBase64(
              media?.previewUrl ||
                additionalMedia?.previewUrl ||
                mobileStaticMedia?.mediaUrl,
            )
              ? 'base64'
              : 'url'
            this.exerciseHover = true
          }
        }, 1000)
      } else this.exerciseClearHover(id)
    },
    exerciseClearHover(id) {
      this.progressBarsValues = { ...this.progressBarsValues, ...{ [id]: 0 } }

      clearInterval(this.exerciseHoverInterval)
      if (!this.exerciseHover) {
        clearTimeout(this.exerciseHoverTimeout)
        this.exerciseHover = false
        this.exerciseHoverUrl = null
        this.exerciseHoverType = 'base64'
      }
    },
    exerciseCloseHover() {
      this.exerciseHover = false
    },
    loadDialogExerciseDefinitions(params) {
      let defaultParams = {
        type: 'EXERCISE',
        page: 0,
        pageSize: 50,
        sortBy: 'creationDate',
        folder: 'All',
      }
      debounce(
        () => {
          this.getDialogExerciseDefinitions({
            params: { ...defaultParams, ...params },
          })
        },
        'getDialogExerciseDefinitions',
        100,
      )
    },
    advancedExerciseSearchFilter(queryParams, mode = true) {
      let currentQueryParams = {}
      Object.keys(this.queryParams).forEach(key => {
        currentQueryParams[key] = null
      })
      let query = {
        ...this.defaultQueryParams,
        ...currentQueryParams,
        ...queryParams,
      }
      this.getDataAction(query)
      if (mode) this.advancedExerciseSearchActive = true
      this.showAdvancedExerciseSearchDialog = false
    },
    advancedSequenceSearchFilter(queryParams, mode = true) {
      let currentQueryParams = {}
      Object.keys(this.queryParams).forEach(key => {
        currentQueryParams[key] = null
      })
      let query = {
        ...this.defaultQueryParams,
        ...currentQueryParams,
        ...queryParams,
      }
      this.getDataAction(query)
      if (mode) this.advancedSequenceSearchActive = true
      this.showAdvancedSequenceSearchDialog = false
    },
    clearFilters() {
      this.advancedExerciseSearchActive = false
      this.advancedSequenceSearchActive = false
      this.clearFilterTrigger++
    },
    tabChange(tab) {
      this.queryParams = { ...this.defaultQueryParams }
      this.clearFilters()
      this.$router.push(this.urls[tab]).catch(() => {})
    },
    tabActivitiesChange(tab) {
      this.tabActivities = tab
      let query = { ...this.defaultQueryParams, ...this.queryParams }
      query.type = 'ACTIVITY'
      if (this.tabActivities == 1) query.type = 'EXERCISE,SERIES'
      this.getDataAction(query)
    },
    importExercise({ exerciseId, type }) {
      let data = { exerciseId: exerciseId, type: type }
      if (this.queryParams?.folderId) data.folderId = this.queryParams.folderId
      this.shareExerciseDefinition(data)
    },
    importSequence(sequenceId) {
      let data = { sequenceId: sequenceId }
      if (this.queryParams?.folderId) data.folderId = this.queryParams.folderId
      this.shareSequenceDefinition(data)
    },
    closeNewExerciseFolderDialog() {
      this.newExerciseFolderName = ''
      this.editExerciseFolderId = false
      this.newExerciseFolderDialog = false
    },
    closeNewSequenceFolderDialog() {
      this.newSequenceFolderName = ''
      this.newSequenceFolderDialog = false
    },
    prepareNewExerciseFolder() {
      if (this.editExerciseFolderId) {
        this.renameExerciseFolder({
          id: this.editExerciseFolderId,
          title: this.newExerciseFolderName,
        })
      } else {
        this.createNewExerciseFolder({ title: this.newExerciseFolderName })
      }
      this.closeNewExerciseFolderDialog()
    },
    prepareNewSequenceFolder() {
      if (this.editSequenceFolderId) {
        this.renameSequenceFolder({
          id: this.editSequenceFolderId,
          title: this.newSequenceFolderName,
        })
      } else {
        this.createNewSequenceFolder({ title: this.newSequenceFolderName })
      }
      this.closeNewSequenceFolderDialog()
    },
    async moveExercisesToFolder(items, folderId) {
      let data = {}
      if (folderId == null) {
        data.folderId = null
        data.rootFolder = true
      } else {
        data.folderId = folderId
        data.rootFolder = false
      }
      if (items) {
        if (!isNaN(items)) {
          this.moveExerciseItem({ itemId: items, data: data })
        } else if (Array.isArray(items)) {
          for await (const item of items) {
            await this.moveExerciseItem({ itemId: item.id, data: data })
          }
        } else {
          return
        }
      }
      await this.getData()
    },
    async moveSequencesToFolder(items, folderId) {
      let data = {}
      if (folderId == null) {
        data.folderId = null
        data.rootFolder = true
      } else {
        data.folderId = folderId
        data.rootFolder = false
      }
      if (items)
        for await (const item of items) {
          await this.moveSequenceItem({ itemId: item, data: data })
        }
      await this.getData()
    },
    openRenameExerciseFolderDialog(id, name) {
      this.editExerciseFolderId = id
      this.newExerciseFolderName = name
      this.newExerciseFolderDialog = true
    },
    openShareFolderItemsDialog(id) {
      this.editExerciseFolderId = id
      this.showShareExerciseDialog = true
    },
    openRenameSequenceFolderDialog(id, name) {
      this.editSequenceFolderId = id
      this.newSequenceFolderName = name
      this.newSequenceFolderDialog = true
    },
    openRemoveExerciseFolderDialog() {
      this.showConfirmDeleteFolderDialog = false
      this.removeExerciseFolderDialog = true
    },
    openRemoveSequenceFolderDialog() {
      this.showConfirmDeleteFolderDialog = false
      this.removeSequenceFolderDialog = true
    },
    prepareRemoveExerciseFolder(completely) {
      this.removeExerciseFolder({
        id: this.deleteFolderId,
        completely: completely,
      })
      if (this.deleteFolderId == this.queryParams.folderId)
        this.searchInput({ key: 'folderId', value: null })
      this.deleteFolderId = null
      this.removeExerciseFolderDialog = false
    },
    prepareRemoveSequenceFolder(completely) {
      this.removeSequenceFolder({
        id: this.deleteFolderId,
        completely: completely,
      })
      if (this.deleteFolderId == this.queryParams.folderId)
        this.searchInput({ key: 'folderId', value: null })
      this.deleteFolderId = null
      this.removeSequenceFolderDialog = false
    },
    async exerciseGetPreview(exercise) {
      await this.getExerciseDefinition({ id: exercise.id, type: exercise.type })
      this.showExercisePreviewDialog = true
    },
    async sequenceGetPreview(sequenceId) {
      await this.getSequenceDefinition({ id: sequenceId })
      this.showSequencePreviewDialog = true
    },
    openMediaPreview(media) {
      this.showBiggerImage({
        url: media.mediaUrl,
        file: media.mediaUrl,
        name: media.filename,
        mlResourceUrl: null,
        type: media.fileTypeId == 19 ? 3 : 1,
      })
    },
    openImportExerciseDialog() {
      this.showImportExerciseDialog = true
    },
    openImportExerciseAdminDialog() {
      this.showImportExerciseCompaniesDialog = true
    },
    openImportSequenceDialog() {
      this.showImportSequenceDialog = true
    },
    paginationChange(input) {
      this.getDataAction({ ...this.defaultQueryParams, page: input })
    },
    reloadExerciseDefinitions() {
      debounce(
        () => {
          this.getData()
        },
        2000,
        'getData',
      )
    },
    openSelectAlternativeExercisesDialog() {
      this.showAlternativeExercisesDialog = true
    },
    closeSelectAlternativeExercisesDialog() {
      this.showAlternativeExercisesDialog = false
      this.alternativeExercisesKey++
    },
    chosenAlternativeExercises(exercises) {
      this.alternativeExercisesList = exercises.filter(item => {
        if (item.selected) return item
      })
    },
    onClickOutsideDeleteExerciseDialog() {
      this.closeDialogs()
      this.exerciseId = null
    },
    onKeydownDeleteExerciseDialog(key) {
      if (key.code === 'Escape' && key.isTrusted)
        this.onClickOutsideDeleteExerciseDialog()
    },
    onClickOutsideNewExerciseFolder() {
      this.closeNewExerciseFolderDialog()
      this.exerciseId = null
    },
    onKeydownNewExerciseFolder(key) {
      if (key.code === 'Escape' && key.isTrusted)
        this.onClickOutsideNewExerciseFolder()
    },
    onClickOutsideRemoveExerciseFolder() {
      this.removeExerciseFolderDialog = false
      this.editExerciseFolderId = null
      this.closeConfirmDeleteFolderDialog()
    },
    onKeydownRemoveExerciseFolder(key) {
      if (key.code === 'Escape' && key.isTrusted)
        this.onClickOutsideRemoveExerciseFolder()
      this.closeConfirmDeleteFolderDialog()
    },
    onClickOutsideRemoveSequenceFolder() {
      this.removeSequenceFolderDialog = false
      this.editSequenceFolderId = null
      this.closeConfirmDeleteFolderDialog()
    },
    onKeydownRemoveSequenceFolder(key) {
      if (key.code === 'Escape' && key.isTrusted)
        this.onClickOutsideRemoveSequenceFolder()
      this.closeConfirmDeleteFolderDialog()
    },
    onClickOutsideNewSquenceFolder() {
      this.closeNewSequenceFolderDialog()
    },
    onKeydownNewSquenceFolder(key) {
      if (key.code === 'Escape' && key.isTrusted)
        this.onClickOutsideNewSquenceFolder()
    },
    onClickOutsideNewSquence() {
      this.closeNewSequenceDialog()
    },
    onKeydownNewSquence(key) {
      if (key.code === 'Escape' && key.isTrusted)
        this.onClickOutsideNewSquence()
    },
    onClickOutsideDeleteSequence() {
      this.closeDialogs()
      this.sequenceId = null
    },
    onKeydownDeleteSequence(key) {
      if (key.code === 'Escape' && key.isTrusted)
        this.onClickOutsideDeleteSequence()
    },
    onClickOutsideRenameSequence() {
      this.closeDialogs()
      this.sequenceId = null
    },
    onKeydownRenameSequence(key) {
      if (key.code === 'Escape' && key.isTrusted)
        this.onClickOutsideRenameSequence()
    },
    onClickOutsideExerciseHover() {
      this.exerciseCloseHover()
    },
    onKeydownExerciseHover(key) {
      if (key.code === 'Escape' && key.isTrusted)
        this.onClickOutsideExerciseHover()
    },
    onClickOutsideShareExerciseBack() {
      this.closeDialogs()
      this.exerciseId = null
    },
    onKeydownShareExerciseBack(key) {
      if (key.code === 'Escape' && key.isTrusted)
        this.onClickOutsideShareExerciseBack()
    },
    openConfirmDeleteDialog(id, type, itemTypeName) {
      this.itemTypeName = itemTypeName
      this.showConfirmDeleteDialog = true
      this.deleteItemId = id
      this.deleteItemType = type
    },
    closeConfirmDeleteDialog() {
      this.showConfirmDeleteDialog = false
    },
    openConfirmDeleteFolderDialog(id, type) {
      this.deleteFolderId = id
      this.deleteFolderType = type
      this.showConfirmDeleteFolderDialog = true
    },
    closeConfirmDeleteFolderDialog() {
      this.showConfirmDeleteFolderDialog = false
    },
    toSmartExercises() {
      this.$router.push({ name: 'Smart Exercises', path: '/smart-activities/' })
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.exercises {
  padding: 2 * $gutter;
  height: 100%;
  display: flex;
  flex-direction: column;
  &-tile {
    display: inline-block;
  }
  &-activities {
    height: 100%;
    display: flex;
    flex-direction: column;
    &.dragging .selected {
      cursor: grabbing !important;
    }
  }
  &-sequences {
    height: 100%;
    display: flex;
    flex-direction: column;
    &__wrapper {
      display: flex;
      margin-left: -$gutter/2;
      margin-right: -$gutter/2;
      flex-wrap: wrap;
      flex-grow: 1;
    }
    &__tile {
      width: 286px;
      height: 216px;
      border: 1px solid $bh-white;
      border-radius: 16px;
      padding: $gutter;
      margin: 8px;
      background: $bh-white;
      position: relative;
      &.bordered {
        border-color: transparent;
        background: transparent;
      }
      .v-btn {
        margin-bottom: 5px;
      }
      h5 {
        color: $bh-black;
      }
    }
    &__menu {
      position: absolute;
      right: $gutter/2;
      top: $gutter/2;
    }
    &__avatar {
      &-list {
        display: flex;
      }
      &-image {
        img {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          object-fit: cover;
          display: block;
        }
        &:nth-child(n + 1) {
          margin-left: -4px;
        }
      }
      &-more {
        line-height: 24px;
        margin-left: 4px;
      }
    }
  }
  &-exercises {
    display: flex;
    flex-wrap: wrap;
    margin-left: -$gutter/2;
    margin-right: -$gutter/2;
    &__wrapper {
      display: flex;
      flex-wrap: wrap;
      flex-grow: 1;
      align-content: flex-start;
    }
  }
  &-heading {
    font-size: 14px;
    color: $bh-gray;
    margin-left: 8px;
    margin-bottom: 0px;
  }
  &-folders {
    padding: 0 $gutter/2;
    margin-bottom: 20px;
    &--folder {
      display: inline-block;
      background: $bh-white;
      border-radius: $gutter * 4;
    }
    .v-btn {
      transition-duration: 200ms;
      transition-property: border, background, opacity;
      border: 1px solid transparent;
      &:first-child {
        width: 240px;
      }
      &.new {
        width: 268px;
      }
      &__content {
        max-width: 210px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        display: block;
      }
      &:hover {
        border-color: rgba(82, 87, 92, 0.08);
      }
      &.drop-active {
        border-color: $bh-blue !important;
      }
      &.dragging:hover {
        cursor: grabbing !important;
      }
      span {
        pointer-events: none;
      }
    }
  }
  &-filters {
    &__toolbar {
      display: flex;
      padding: $gutter $gutter/2;
    }
    .v-btn {
      background: $bh-gray--6;
      margin-right: $gutter/2;
    }
    &__button-group {
      background: $bh-gray--6;
      border-radius: 50px;
      margin-left: $gutter/2;
      padding: 0 $gutter/2 0 0;
      &.disabled {
        background: rgba(255, 255, 255, 0.3);
      }
      & > .v-btn:first-child {
        margin-left: 0;
      }
    }
  }
  &__tabs {
    &.v-tabs {
      width: 600px;
      margin: 0 auto;
      background: $bh-white;
      border-radius: 9999px;
      .v-tabs-slider-wrapper {
        height: 100% !important;
        border-radius: 999px !important;
        overflow: hidden;
        z-index: -1;
      }
      .v-slide-group__wrapper {
        background: $bh-white;
        border-radius: 999px;
      }
      & > .v-tabs-bar {
        .v-tab {
          transition-duration: 500ms !important;
          transition-property: color;
        }
        .v-tab.v-tab--active {
          color: rgba(255, 255, 255, 1);
        }
        .v-tab:hover::before {
          display: none !important;
        }
        background: $bh-blue--4 !important;
      }
    }
  }
  &__tabs-secondary {
    margin-top: 15px;
    background: transparent;
    & > .v-tabs-bar {
      background: $bh-blue--4 !important;
    }
  }
  &__tab-items {
    background: $bh-blue--4 !important;
    height: 100%;
    .v-window-item {
      height: 100%;
    }
  }
  .exercises-hover-dialog {
    width: auto;
    padding: 10px;
    background: $bh-white;
    .v-btn {
      position: absolute;
      top: 10px;
      right: 10px;
      width: 32px;
      height: 32px;
      background: $bh-blue;
    }
    img {
      display: block;
      max-height: 80vh;
      max-width: 1000px;
    }
  }
  .v-pagination {
    &__item,
    &__navigation {
      box-shadow: none;
    }
  }
  .v-speed-dial__list {
    align-items: flex-end;
    .v-btn {
      color: $bh-white;
      .v-icon {
        margin-right: 4px;
      }
    }
  }
  &__dialog__list {
    min-height: auto;
  }
}
</style>
